import { convexQuery } from "@convex-dev/react-query";
import type { Id } from "@convex/_generated/dataModel";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";

import {
  locationDiscountsDeleteDiscount,
  locationDiscountsGetAllByLocationId,
} from "~/api-refs";
import { ModalLoadingDialog } from "~/components/shared/modal-loading-dialog";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { ProductEvents } from "~/lib/posthog";

export const Route = createFileRoute(
  "/_authenticated/locations/$id/discounts/$discountId/delete"
)({
  component: DeleteLocationDiscountModal,
});

function DeleteLocationDiscountModal() {
  const { id: locationId, discountId } = Route.useParams();
  const navigate = useNavigate();

  // Fetch the discount to display
  const { data: discountData } = useQuery(
    convexQuery(locationDiscountsGetAllByLocationId, {
      locationId: toConvexTableId("locations", locationId),
    })
  );

  const restaurantDiscount = discountData?.discounts.find(
    (d) => d._id === discountId
  );

  const deleteMutation = useMutation({
    mutationFn: (data: {
      locationId: Id<"locations">;
      locationDiscountId: Id<"locationDiscounts">;
    }) => {
      return convexClient.mutation(locationDiscountsDeleteDiscount, data);
    },
    onSuccess: () => {
      ProductEvents.discountDeleted(discountId);
    },
  });

  const handleClose = () => {
    void navigate({ to: `/locations/${locationId}/discounts` });
  };

  const handleDelete = async () => {
    if (deleteMutation.isPending || !restaurantDiscount) {
      return;
    }
    try {
      await deleteMutation.mutateAsync({
        locationId: toConvexTableId("locations", locationId),
        locationDiscountId: toConvexTableId("locationDiscounts", discountId),
      });
      toast.success("Discount deleted", {
        description: `${restaurantDiscount.name} discount has been deleted from POS devices at this location.`,
      });
      handleClose();
    } catch {
      toast.error("Error deleting discount");
    }
  };

  if (!restaurantDiscount) {
    return (
      <ModalLoadingDialog
        message="Loading discount details..."
        onOpenChange={handleClose}
      />
    );
  }

  return (
    <Dialog onOpenChange={handleClose} open>
      <DialogContent className="max-w-120 space-y-5">
        <DialogHeader className="space-y-1">
          <DialogTitle className="h2 font-heading text-foreground text-center">
            Delete Discount
          </DialogTitle>
          <DialogDescription className="text-foreground text-center">
            Are you sure you want to delete this discount?
          </DialogDescription>
        </DialogHeader>
        <div className="bg-muted w-full space-y-1 rounded-2xl px-5 py-4">
          <p className="subtitle-sm text-foreground/70">
            {restaurantDiscount.name}
          </p>
          <p className="text-foreground">
            {restaurantDiscount.precision === "amount" ? "$ " : ""}
            {restaurantDiscount.value}
            {restaurantDiscount.precision === "percentage" ? " %" : ""}
          </p>
        </div>
        <div className="flex w-full justify-between space-x-3">
          <Button
            className="h-9 w-full"
            onClick={handleClose}
            type="button"
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            className="h-9 w-full"
            disabled={deleteMutation.isPending}
            onClick={handleDelete}
            type="button"
            variant="destructive"
          >
            Delete
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
